import { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { Spacing4, Spacing16 } from 'autogen/design-tokens/tokens';
import TextArea from 'components/shared/inputs/text/TextArea';
import { customScrollbar } from 'modules/helpers/mixins';

type AutoExpandTextAreaProps = React.ComponentPropsWithoutRef<typeof TextArea>;

const MAX_ROWS = 4;
const MIN_ROWS = 2;
const PADDING = Spacing16;

const commonStyles = css`
    border: none;
    outline: none;
    background: none;
    font: inherit;
    min-width: 0;
    padding: ${PADDING};
    padding-right: ${Spacing4};
    scroll-padding-bottom: ${PADDING};
    max-height: calc(${MAX_ROWS}lh + calc(${PADDING} * 2)); 
    min-height: calc(${MIN_ROWS}lh + calc(${PADDING} * 2));
    align-content: center;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    scrollbar-gutter: stable;
    font-size: 16px;
    ${customScrollbar()}

    /* Place on top of each other */
    grid-area: 1 / 1 / 2 / 2;
`;

const GrowWrapper = styled.div`
    display: grid;
    height: min-content;
    flex-grow: 1;

    &::after {
        content: attr(data-replicated-value) ' ';
        visibility: hidden;
        ${commonStyles}
    }


`;

const StyledTextArea = styled(TextArea)`
    resize: none;
    ${commonStyles}

    &:focus, &:focus-visible, &:active {
        outline: none;
    }
`;

const AutoExpandTextArea = forwardRef<HTMLTextAreaElement, AutoExpandTextAreaProps>(
    (props: AutoExpandTextAreaProps, ref) => {
        const { value, ...restProps } = props;

        return (
            <GrowWrapper data-replicated-value={value}>
                <StyledTextArea ref={ref} value={value} {...restProps} />
            </GrowWrapper>
        );
    }
);

export default AutoExpandTextArea;
